import { useParams, Navigate } from 'react-router-dom';
import { NavOverlay } from '@/components/NavOverlay';
import { SplitGallery } from '@/components/SplitGallery';
import { VerticalGallery } from '@/components/VerticalGallery';
import { HorizontalGallery } from '@/components/HorizontalGallery';
import { useProjectImages } from '@/hooks/useProjects';
import { useResolveUserId } from '@/hooks/useResolveUserId';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export default function ProjectPage() {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { userId } = useResolveUserId();
  const [project, setProject] = useState<any>(null);
  const [loadingProject, setLoadingProject] = useState(true);

  useEffect(() => {
    if (!userId || !projectSlug) return;

    supabase
      .from('projects')
      .select('*')
      .eq('slug', projectSlug)
      .eq('user_id', userId)
      .eq('published', true)
      .maybeSingle()
      .then(({ data }) => {
        setProject(data);
        setLoadingProject(false);
      });
  }, [userId, projectSlug]);

  const { pairs, loading: loadingImages } = useProjectImages(project?.id);

  // Contextual page title
  usePageTitle(project?.name);

  const isLoading = loadingProject || (!!project && loadingImages);

  if (!loadingProject && !project) {
    return <Navigate to="/" replace />;
  }

  if (!loadingProject && !!project && !loadingImages && pairs.length === 0) {
    return <Navigate to="/" replace />;
  }

  const layoutType = project?.layout_type || 'A';

  const renderGallery = () => {
    switch (layoutType) {
      case 'B':
      case 'vertical-scroll':
        return <VerticalGallery pairs={pairs} sectionTitle={project?.name} />;
      case 'C':
      case 'horizontal-scroll':
        return <HorizontalGallery pairs={pairs} sectionTitle={project?.name} />;
      case 'D':
      case 'split-horizontal':
        return <SplitGallery pairs={pairs} sectionTitle={project?.name} showPairTitle layout="horizontal" />;
      case 'A':
      case 'split-vertical':
      default:
        return <SplitGallery pairs={pairs} sectionTitle={project?.name} showPairTitle />;
    }
  };

  return (
    <>
      <LoadingSpinner visible={isLoading} />
      <NavOverlay />
      {!isLoading && renderGallery()}
    </>
  );
}
